import {
  LINE_ITEM_ANNUAL_PASS_VOUCHER,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_CUSTOMER_COMMISSION_EXTRA_LINE_ITEMS,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_PROVIDER_NIGHTLY_COMMISSION,
} from '../util/types';
import { PASS_ANNUAL_TWENTY_FIVE_PASS_VOUCHER } from '../util/annualPass';
import { nativeAppPlatform } from './nativeFirebase';

// Spiegel von server/api-util/purchaseTracking.js (Buchungsteil) — Änderungen immer in beiden
// Dateien. Browser und Server müssen für dieselbe Transaktion identische Parameter berechnen
// (GA4/Meta dedupen über die Transaktions-ID, der erste Wert gewinnt). Beträge intern in
// ganzzahligen Cent, erst beim Bauen der Parameter in Euro umgerechnet.

export const COMMISSION_VAT_RATE = 0.19;

const CUSTOMER_COMMISSION_CODES = [
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_CUSTOMER_COMMISSION_EXTRA_LINE_ITEMS,
];
const PROVIDER_COMMISSION_CODES = [
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_PROVIDER_NIGHTLY_COMMISSION,
];

const toEuro = (cents) => cents / 100;

const netFromGross = (grossCents, rate) => {
  const netCents = Math.round(grossCents / (1 + rate));
  return { netCents, taxCents: grossCents - netCents };
};

const sum = (values) => values.reduce((total, value) => total + value, 0);

const linesOf = (transaction) => transaction.attributes.lineItems || [];

const lineCents = (lineItem) => lineItem.lineTotal?.amount || 0;

const isCustomerLine = (lineItem) => (lineItem.includeFor || []).includes('customer');

const activeLines = (transaction) => linesOf(transaction).filter((li) => !li.reversal);

// Gast-Provision (positiv) plus Gastgeber-Provision (negativ, als Betrag gezählt)
const commissionGrossCents = (transaction) => {
  const lines = activeLines(transaction);
  const customer = lines.filter((li) => CUSTOMER_COMMISSION_CODES.includes(li.code));
  const provider = lines.filter((li) => PROVIDER_COMMISSION_CODES.includes(li.code));
  return sum(customer.map(lineCents)) + sum(provider.map((li) => Math.abs(lineCents(li))));
};

// Pass-Rabatt als positiver Cent-Betrag (Kunden-Zeilen mit negativem lineTotal)
const passDiscountCents = (transaction) =>
  sum(
    activeLines(transaction)
      .filter(
        (li) =>
          li.code === LINE_ITEM_ANNUAL_PASS_VOUCHER && isCustomerLine(li) && lineCents(li) < 0
      )
      .map((li) => Math.abs(lineCents(li)))
  );

const isTwentyFivePass = (transaction) => {
  const metadata = transaction.attributes.metadata || {};
  // Altbuchungen tragen den Typ noch unter `voucher` (vor der Umbenennung)
  return [metadata.annualPassVoucher?.type, metadata.voucher?.type].includes(
    PASS_ANNUAL_TWENTY_FIVE_PASS_VOUCHER
  );
};

// Fehlt der Typ trotz Pass-Rabatt, zählt die Buchung als Pass-Buchung (Spec §6.0)
export const isPassBooking = (transaction) =>
  passDiscountCents(transaction) > 0 && !isTwentyFivePass(transaction);

const payinOf = (transaction) => transaction.attributes.payinTotal || {};

const currencyOf = (transaction) => String(payinOf(transaction).currency || '').toLowerCase();

export const bookingPurchaseValues = (transaction) => {
  const { netCents, taxCents } = netFromGross(
    commissionGrossCents(transaction),
    COMMISSION_VAT_RATE
  );
  return {
    valueCents: netCents,
    taxCents,
    bookingVolumeCents: payinOf(transaction).amount || 0,
    passDiscountCents: passDiscountCents(transaction),
  };
};

// Firebase verwirft den Wert 0 ohnehin, damit Server und Browser gleich bleiben, fehlt der Key
const optionalPositive = (key, cents) => (cents > 0 ? { [key]: toEuro(cents) } : {});

export const bookingPurchaseParams = (transaction, platform) => {
  const values = bookingPurchaseValues(transaction);
  return {
    value: toEuro(values.valueCents),
    tax: toEuro(values.taxCents),
    currency: currencyOf(transaction),
    purchase_type: 'booking',
    product: 'listing',
    platform,
    ...optionalPositive('booking_volume', values.bookingVolumeCents),
    ...optionalPositive('pass_discount', values.passDiscountCents),
  };
};

// Pass-Buchungen tragen bewusst weder value noch tax (Spec E3)
export const passBookingParams = (transaction, platform) => {
  const values = bookingPurchaseValues(transaction);
  return {
    currency: currencyOf(transaction),
    product: 'listing',
    platform,
    pass_discount: toEuro(values.passDiscountCents),
    ...optionalPositive('booking_volume', values.bookingVolumeCents),
  };
};

export const clientPlatform = () => nativeAppPlatform() || 'web';
